import styled from '@emotion/styled'
import {
    ApiShopperLandingPageLink,
    hasRealVariantSelection,
    hasSwatchData,
} from '@storefronts/common-types'
import React, {
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import {
    BUTTON_BORDER_RADIUS_VAR,
    PRIMARY_CTA_FILL_COLOR_VAR,
    PRIMARY_CTA_TEXT_COLOR_VAR,
    PRIMARY_CTA_BORDER_VAR,
    PRIMARY_ICON_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    CustomizationContext,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    TERTIARY_CTA_BORDER_VAR,
    TERTIARY_CTA_FILL_COLOR_VAR,
    TERTIARY_CTA_TEXT_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import {
    buildCartVariant,
    findCartItem,
    isCartItemEditDisabled,
} from '../../../utils/cartTools'
import { getNumberOfAvailableSwatchOptions } from '../../shared/variant-options/ProductGridColorSwatches'
import { ProductContent, type ProductData } from '../products/ProductContent'
import { ProductContext } from '../../../context/productContext'
import {
    Dialog,
    DialogTrigger,
    DialogOverlay,
    DialogClose,
    DialogHandle,
} from '../../shared/Dialog'
import { CartContext } from '../../../context/cartContext'
import { QuantityStepper } from './QuantityStepper'
import { CloseIcon } from '../../icons/Close'
import { QueryParamContext } from '../../../context/QueryParamContextProvider'
import { useFullLandingPageLink } from '../../../hooks/useFullLandingPageLink'
import { getTypographyRoleStyle } from '../../../style/typography'
import { scaledFontSize } from '../../../style/scrollingLayout'
import { PageContext } from '../../../context/PageContextProvider'
import { useDesktopModal } from '../../../hooks/useDesktopModal'
import { dialogSheet } from '../../shared/overlayModal'
import {
    darken,
    hoverDarkenFill,
    hoverFill,
    hoverInvertOutline,
} from '../../../style/interactiveStates'

const CloseIconButton = styled.button({
    background: 'white',
    borderRadius: '100%',
    padding: '4px',
    border: 'none',
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 1,
    cursor: 'pointer',
    ...hoverFill('#ffffff', `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`),
})

const VariantSelectDialogContent = styled.div({
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    borderRadius: '0.2rem',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    ...dialogSheet,
})

const ScrollableContent = styled.div({
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
})

const DISABLED_GREY = '#D3D3D3'

// The grid ATC uses the brand's tertiary CTA colors when configured; otherwise
// it renders as a hollow button (transparent fill, page text color for label
// and border). CSS `var(name, fallback)` picks the right branch without JS
// having to know whether the tertiary vars are defined.
const SmallCTAButton = styled.button<{ disabled?: boolean }>(
    ({ disabled = false }) => ({
        padding: 8,
        ...getTypographyRoleStyle('buttonText', {
            fontWeight: 400,
        }),
        fontSize: scaledFontSize(13),
        borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: 34,
        textAlign: 'center',
        pointerEvents: 'auto',
        backgroundColor: disabled
            ? 'transparent'
            : `var(${TERTIARY_CTA_FILL_COLOR_VAR}, transparent)`,
        color: disabled
            ? DISABLED_GREY
            : `var(${TERTIARY_CTA_TEXT_COLOR_VAR}, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}))`,
        border: disabled
            ? `1px solid ${DISABLED_GREY}`
            : `var(${TERTIARY_CTA_BORDER_VAR}, 1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR}))`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxSizing: 'border-box',
        // One pair of declarations covers both branches: with the tertiary
        // vars set the button is filled and only darkens, and without them the
        // fallbacks promote the outline colour to the fill, which is the
        // inversion a hollow button wants.
        ...hoverInvertOutline(
            darken(
                `var(${TERTIARY_CTA_FILL_COLOR_VAR}, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}))`,
            ),
            `var(${TERTIARY_CTA_TEXT_COLOR_VAR}, var(${PRIMARY_PAGE_FILL_COLOR_VAR}))`,
        ),
    }),
)

type CTAButtonState = 'idle' | 'pending' | 'disabled'
const StickyCTAButton = styled.button<{ state?: CTAButtonState }>(
    ({ state = 'idle' }) => {
        let background = `var(${PRIMARY_CTA_FILL_COLOR_VAR})`
        let color = `var(${PRIMARY_CTA_TEXT_COLOR_VAR})`
        let border = `var(${PRIMARY_CTA_BORDER_VAR})`
        let cursor = 'pointer'
        if (state === 'disabled') {
            background = DISABLED_GREY
            border = 'none'
            cursor = 'not-allowed'
        } else if (state === 'pending') {
            // Product Added state: transparent bg, black text, border
            background = 'transparent'
            color = `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
            border = `2px solid var(${PRIMARY_CTA_FILL_COLOR_VAR})`
            cursor = 'not-allowed'
        }
        return {
            position: 'sticky',
            bottom: '1rem',
            padding: 14,
            margin: '24px',
            boxSizing: 'border-box',
            background,
            color,
            border,
            borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
            fontSize: scaledFontSize(16),
            ...getTypographyRoleStyle('buttonText', {
                fontWeight: 400,
            }),
            cursor,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 48,
            textAlign: 'center',
            ...(state === 'idle' && hoverDarkenFill(background)),
        }
    },
)

/**
 * Shared decision used by product grid and cart upsells: does adding this
 * product to the cart require picking options in the intermediate modal?
 * (Multi-variant, real option groups, and not the single-swatch inline bypass.)
 */
export const needsVariantSelectionModal = (
    lpl: ApiShopperLandingPageLink,
): boolean => {
    const meaningfulOptions = (lpl.optionInfo ?? [])
        .slice(0, 3)
        .filter((opt) => opt.values.length > 1)
    const bypassModalForInlineSwatch =
        meaningfulOptions.length === 1 &&
        hasSwatchData(meaningfulOptions[0]) &&
        getNumberOfAvailableSwatchOptions(lpl) > 1

    return (
        Number(lpl.variantInfo?.length) > 1 &&
        (lpl.optionInfo == null ||
            (hasRealVariantSelection(lpl.optionInfo) &&
                !bypassModalForInlineSwatch))
    )
}

/**
 * Mini-PDP dialog for picking a variant/purchase option before adding to cart.
 * Renders {@link ProductContent} plus a sticky "Add to Cart" button; the caller
 * owns the cart mutation via `onAddToCart` and can supply a custom `trigger`
 * or open the dialog programmatically through `innerRef`.
 */
export const VariantSelectDialog: React.FC<{
    link: ApiShopperLandingPageLink
    productData: ProductData
    onAddToCart: () => void
    trigger?: React.ReactNode
    innerRef?: React.Ref<DialogHandle>
}> = ({ link: lpl, productData, onAddToCart, trigger, innerRef }) => {
    const { activeVariant } = productData
    const [isProductAdded, setIsProductAdded] = useState(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const dialogRef = useRef<DialogHandle>(null)

    // Forward both close (for the auto-dismiss timer below) and open (for
    // callers that trigger the dialog programmatically instead of via `trigger`).
    useImperativeHandle(
        innerRef,
        () => ({
            open: () => dialogRef.current?.open(),
            close: () => dialogRef.current?.close(),
        }),
        [],
    )

    const handleAddToCart = () => {
        onAddToCart()
        setIsProductAdded(true)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            dialogRef.current?.close()
            setIsProductAdded(false)
        }, 1500)
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    let buttonText: React.ReactNode = 'Add to Cart'
    let buttonState: CTAButtonState = 'idle'
    if (isProductAdded) {
        buttonText = 'Product Added'
        buttonState = 'pending'
    } else if (!activeVariant?.available) {
        buttonText = 'Out of Stock'
        buttonState = 'disabled'
    }

    return (
        <Dialog ref={dialogRef}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogOverlay>
                <VariantSelectDialogContent>
                    <DialogClose asChild>
                        <CloseIconButton>
                            <CloseIcon
                                width={24}
                                height={24}
                                fill={`var(${PRIMARY_ICON_COLOR_VAR})`}
                            />
                        </CloseIconButton>
                    </DialogClose>
                    <ScrollableContent>
                        <VariantSelectProductContent
                            link={lpl}
                            productData={productData}
                        />
                        <StickyCTAButton
                            onClick={handleAddToCart}
                            disabled={
                                !activeVariant?.available || isProductAdded
                            }
                            state={buttonState}
                        >
                            {buttonText}
                        </StickyCTAButton>
                    </ScrollableContent>
                </VariantSelectDialogContent>
            </DialogOverlay>
        </Dialog>
    )
}

const VariantSelectProductContent: React.FC<{
    link: ApiShopperLandingPageLink
    productData: ProductData
}> = ({ link, productData }) => {
    const fullLink = useFullLandingPageLink(link)
    const {
        activeVariant,
        setActiveVariant,
        price,
        comparePrice,
        reviewsRef,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    } = productData
    const { HIDE_CART_OVERRIDE } = useContext(QueryParamContext)
    const { displayPoweredByFooter } = useContext(CustomizationContext)

    if (!fullLink) return null

    return (
        <ProductContext.Provider
            value={{
                reviewsRef: reviewsRef as React.RefObject<HTMLDivElement>,
            }}
        >
            <ProductContent
                lpLink={fullLink}
                activeVariant={activeVariant}
                setActiveVariant={setActiveVariant}
                reviewsRef={reviewsRef}
                price={price}
                comparePrice={comparePrice}
                discountPercentage={discountPercentage}
                hideCartOverride={HIDE_CART_OVERRIDE}
                displayPoweredByFooter={displayPoweredByFooter}
                maxImageHeight="50vh"
                selectedSellingPlanId={selectedSellingPlanId}
                onSellingPlanSelect={setSelectedSellingPlanId}
            />
        </ProductContext.Provider>
    )
}

const MultiVariantCTA: React.FC<{
    link: ApiShopperLandingPageLink
    productData: ProductData
}> = ({ link: lpl, productData }) => {
    const { Analytics } = useContext(AnalyticsContext)
    const { addCartItem } = useContext(CartContext)
    const { landingPage } = useContext(ViddyLPContext)
    const { setActiveLPLink } = useContext(PageContext)
    const desktopModal = useDesktopModal()

    const handleAddToCart = () => {
        const variant = buildCartVariant(
            productData.activeVariant,
            lpl,
            landingPage.discount ?? null,
        )
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'email_vlp_primary',
            location: 'pdp',
        })
        addCartItem(variant, 1, productData.selectedSellingPlanId)
    }

    // The product already opens as a modal here, so picking a variant uses that
    // rather than a second sheet that shows a cut-down copy of the same product.
    if (desktopModal) {
        return (
            <SmallCTAButton
                onClick={() =>
                    setActiveLPLink(lpl, {
                        initialVariantShopifyId:
                            productData.activeVariant?.shopifyId ?? undefined,
                    })
                }
            >
                Add to Cart
            </SmallCTAButton>
        )
    }

    return (
        <VariantSelectDialog
            link={lpl}
            productData={productData}
            onAddToCart={handleAddToCart}
            trigger={<SmallCTAButton>Add to Cart</SmallCTAButton>}
        />
    )
}

/** PLP grid rows must pass `rowProductData` from {@link useProductGridVariantSelection} (same `useProductData` as the row). */
export const AddToCartInlineCTA: React.FC<{
    link: ApiShopperLandingPageLink
    rowProductData: ProductData
}> = ({ link: lpl, rowProductData }) => {
    const { cart, addCartItem, updateCartItem, removeCartItem } =
        useContext(CartContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { landingPage } = useContext(ViddyLPContext)
    const { activeVariant, selectedSellingPlanId } = rowProductData

    if (activeVariant?.available === false) {
        return <SmallCTAButton disabled>Out of Stock</SmallCTAButton>
    } else if (needsVariantSelectionModal(lpl)) {
        return <MultiVariantCTA link={lpl} productData={rowProductData} />
    } else {
        const variant = buildCartVariant(
            activeVariant,
            lpl,
            landingPage.discount ?? null,
        )
        // Product cards add the brand's default purchase option (one-time
        // unless the brand defaults subscribable products to subscribe), so the
        // stepper tracks that same line; other purchase options for the variant
        // are managed in the cart, not here.
        const defaultSellingPlanId = selectedSellingPlanId
        const existingItem = findCartItem(cart.items, variant, {
            sellingPlanId: defaultSellingPlanId,
        })
        if (existingItem?.variant.available) {
            const itemNotReady = isCartItemEditDisabled(existingItem)
            return (
                <QuantityStepper
                    quantity={existingItem.quantity}
                    setQuantity={(quantity: number) => {
                        if (quantity !== existingItem.quantity)
                            updateCartItem(
                                variant,
                                quantity,
                                undefined,
                                false,
                                undefined,
                                defaultSellingPlanId,
                                existingItem.shopifyLineId,
                            )
                    }}
                    onDelete={() => {
                        removeCartItem(
                            variant,
                            defaultSellingPlanId,
                            existingItem.shopifyLineId,
                        )
                    }}
                    disabled={itemNotReady}
                    loading={itemNotReady}
                />
            )
        } else {
            return (
                <SmallCTAButton
                    onClick={() => {
                        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
                            type: 'email_vlp_primary',
                            location: 'inline',
                        })
                        addCartItem(variant, 1, defaultSellingPlanId)
                    }}
                >
                    Add to Cart
                </SmallCTAButton>
            )
        }
    }
}
